/**
 * P2.6-2: Room Operations Console — Master-Detail Layout
 *
 * Two-panel design for hotel staff:
 * - LEFT:  Scrollable room directory (one row per room)
 * - RIGHT: Selected room detail with Current Stay + Past Stays tabs
 *
 * Scales to 200+ rooms. All data sourced from:
 * - /api/pms-sandbox/rooms (room directory with current guest/stay)
 * - /api/activity-ledger   (all activities, filterable by room)
 */

import { useEffect, useState, useMemo, useCallback } from 'react';
import { ActivityRecord } from '@/types/activityLedger';
import {
  fetchActivityLedger,
  fetchRoomDirectory,
  RoomDirectoryEntry,
} from '@/staff/services/activityLedgerService';
import { RoomDetailPanel } from '@/staff/components/RoomDetailPanel';

// =============================================================================
// TYPES
// =============================================================================

type RoomFilter = 'ALL' | 'OCCUPIED' | 'VACANT' | 'CHECKOUT_TODAY' | 'OUT_OF_SERVICE';

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function RoomOperationsPage() {
  // Data
  const [rooms, setRooms] = useState<RoomDirectoryEntry[]>([]);
  const [activities, setActivities] = useState<ActivityRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI State
  const [selectedRoomNumber, setSelectedRoomNumber] = useState<string | null>(null);
  const [roomFilter, setRoomFilter] = useState<RoomFilter>('OCCUPIED');
  const [searchQuery, setSearchQuery] = useState('');

  // Load all data
  const loadData = useCallback(async () => {
    try {
      setError(null);
      const [roomData, activityData] = await Promise.all([
        fetchRoomDirectory(),
        fetchActivityLedger({ limit: 2000 }),
      ]);

      setRooms(roomData);
      setActivities(activityData);

      console.log('[OPS] Loaded ' + roomData.length + ' rooms, ' + activityData.length + ' activities');
    } catch (err) {
      console.error('[OPS] Failed to load data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadData();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Today's date for checkout filter
  const today = useMemo(() => {
    const d = new Date();
    return d.toISOString().split('T')[0]; // YYYY-MM-DD
  }, []);

  // Filter rooms
  const filteredRooms = useMemo(() => {
    let result = rooms;

    // Status filter
    switch (roomFilter) {
      case 'OCCUPIED':
        result = result.filter(r => r.status === 'OCCUPIED');
        break;
      case 'VACANT':
        result = result.filter(r => r.status === 'VACANT' || r.status === 'CLEANING');
        break;
      case 'CHECKOUT_TODAY':
        result = result.filter(r => {
          if (!r.stay) return false;
          const checkOut = new Date(r.stay.checkOutDate).toISOString().split('T')[0];
          return checkOut === today;
        });
        break;
      case 'OUT_OF_SERVICE':
        result = result.filter(r => r.status === 'OUT_OF_SERVICE');
        break;
    }

    // Search by room number or guest name
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(r => {
        const guestName = r.guest ? (r.guest.firstName + ' ' + r.guest.lastName).toLowerCase() : '';
        return r.roomNumber.includes(q) || guestName.includes(q);
      });
    }

    // Sort by room number
    return result.sort((a, b) => parseInt(a.roomNumber) - parseInt(b.roomNumber));
  }, [rooms, roomFilter, searchQuery, today]);

  // Selected room data
  const selectedRoom = useMemo(
    () => rooms.find(r => r.roomNumber === selectedRoomNumber) ?? null,
    [rooms, selectedRoomNumber]
  );

  // Activities for selected room
  const selectedRoomActivities = useMemo(
    () => activities.filter(a => a.roomNumber === selectedRoomNumber),
    [activities, selectedRoomNumber]
  );

  // Occupancy stats
  const stats = useMemo(() => {
    const occupied = rooms.filter(r => r.status === 'OCCUPIED').length;
    const checkingOutToday = rooms.filter(r => {
      if (!r.stay) return false;
      const checkOut = new Date(r.stay.checkOutDate).toISOString().split('T')[0];
      return checkOut === today;
    }).length;
    return {
      total: rooms.length,
      occupied,
      vacant: rooms.filter(r => r.status === 'VACANT' || r.status === 'CLEANING').length,
      outOfService: rooms.filter(r => r.status === 'OUT_OF_SERVICE').length,
      checkingOutToday,
      occupancyRate: rooms.length > 0 ? Math.round((occupied / rooms.length) * 100) : 0,
    };
  }, [rooms, today]);

  // Filter tab counts
  const filterCounts = useMemo(() => ({
    ALL: rooms.length,
    OCCUPIED: rooms.filter(r => r.status === 'OCCUPIED').length,
    VACANT: rooms.filter(r => r.status === 'VACANT' || r.status === 'CLEANING').length,
    CHECKOUT_TODAY: rooms.filter(r => {
      if (!r.stay) return false;
      const checkOut = new Date(r.stay.checkOutDate).toISOString().split('T')[0];
      return checkOut === today;
    }).length,
    OUT_OF_SERVICE: rooms.filter(r => r.status === 'OUT_OF_SERVICE').length,
  }), [rooms, today]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-180px)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading rooms...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-120px)]">
      {/* Top Bar */}
      <div className="flex-shrink-0 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-2xl font-semibold text-white">Rooms</h1>
            <p className="text-slate-400 text-sm mt-0.5">
              {stats.occupied}/{stats.total} occupied ({stats.occupancyRate}%)
              {stats.checkingOutToday > 0 && (
                <span className="text-yellow-400 ml-2">
                  {'• ' + stats.checkingOutToday + ' checking out today'}
                </span>
              )}
            </p>
          </div>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-3 mb-3">
            <p className="text-red-400 text-sm">{'Warning: ' + error}</p>
          </div>
        )}
      </div>

      {/* Main Content: Two-Panel Split */}
      <div className="flex-1 flex gap-0 min-h-0 rounded-lg overflow-hidden border border-slate-700">
        {/* LEFT PANEL: Room List */}
        <div className={'flex flex-col bg-slate-800 transition-all ' + (selectedRoom ? 'w-[380px] flex-shrink-0' : 'flex-1')}>
          {/* Search + Filters */}
          <div className="p-3 border-b border-slate-700 flex-shrink-0 space-y-3">
            {/* Search */}
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                id="room-ops-search"
                name="room-ops-search"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Room number or guest name..."
                autoComplete="off"
                className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-emerald-500"
              />
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-1 flex-wrap">
              {([
                { id: 'ALL' as RoomFilter, label: 'All' },
                { id: 'OCCUPIED' as RoomFilter, label: 'Occupied' },
                { id: 'VACANT' as RoomFilter, label: 'Vacant' },
                { id: 'CHECKOUT_TODAY' as RoomFilter, label: 'Checkout Today' },
                { id: 'OUT_OF_SERVICE' as RoomFilter, label: 'OOS' },
              ]).map(tab => {
                const count = filterCounts[tab.id];
                const isActive = roomFilter === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setRoomFilter(tab.id)}
                    className={'px-2.5 py-1 text-xs font-medium rounded-full transition-colors ' + (
                      isActive
                        ? 'bg-emerald-900/50 text-emerald-300 border border-emerald-700'
                        : 'bg-slate-900 text-slate-400 border border-slate-700 hover:text-slate-300 hover:border-slate-600'
                    )}
                  >
                    {tab.label}
                    {count > 0 && <span className="ml-1 opacity-70">{count}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Room List */}
          <div className="flex-1 overflow-y-auto">
            {filteredRooms.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm">
                No rooms matching filter
              </div>
            ) : (
              <div className="divide-y divide-slate-700/50">
                {filteredRooms.map(room => (
                  <RoomRow
                    key={room.roomNumber}
                    room={room}
                    isSelected={selectedRoomNumber === room.roomNumber}
                    activityCount={activities.filter(a => a.roomNumber === room.roomNumber).length}
                    onClick={() => setSelectedRoomNumber(
                      selectedRoomNumber === room.roomNumber ? null : room.roomNumber
                    )}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT PANEL: Room Detail */}
        {selectedRoom ? (
          <div className="flex-1 min-w-0">
            <RoomDetailPanel
              room={selectedRoom}
              activities={selectedRoomActivities}
              onClose={() => setSelectedRoomNumber(null)}
            />
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-slate-900/50 text-slate-500">
            <div className="text-center">
              <div className="text-5xl mb-4">🏨</div>
              <p className="text-lg font-medium text-slate-400">Select a room</p>
              <p className="text-sm mt-1">Click on a room to see guest details and activity</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// ROOM ROW
// =============================================================================

interface RoomRowProps {
  room: RoomDirectoryEntry;
  isSelected: boolean;
  activityCount: number;
  onClick: () => void;
}

const STATUS_DOT: Record<string, { color: string; pulse?: boolean }> = {
  OCCUPIED: { color: 'bg-emerald-400' },
  RESERVED: { color: 'bg-purple-400' },
  VACANT: { color: 'bg-slate-500' },
  CLEANING: { color: 'bg-yellow-400', pulse: true },
  OUT_OF_SERVICE: { color: 'bg-red-400' },
};

function RoomRow({ room, isSelected, activityCount, onClick }: RoomRowProps) {
  // RESERVED rooms have no `room.guest` — their guest is in `room.upcomingGuest`
  const activeGuest = room.guest ?? room.upcomingGuest;
  const guestName = activeGuest
    ? activeGuest.firstName + ' ' + activeGuest.lastName
    : null;

  const dotConfig = STATUS_DOT[room.status] ?? { color: 'bg-slate-500' };

  const formatShortDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Dates: use active stay or fall back to upcomingStay for RESERVED rooms
  const displayStay = room.stay ?? room.upcomingStay ?? null;

  // Check if checkout is today
  const isCheckoutToday = displayStay
    ? new Date(displayStay.checkOutDate).toISOString().split('T')[0] === new Date().toISOString().split('T')[0]
    : false;

  return (
    <button
      onClick={onClick}
      className={'w-full px-3 py-3 flex items-center gap-3 text-left transition-colors ' + (
        isSelected
          ? 'bg-emerald-900/20 border-l-2 border-l-emerald-500'
          : 'hover:bg-slate-700/50 border-l-2 border-l-transparent'
      )}
    >
      {/* Status dot */}
      <div className="flex-shrink-0 relative">
        <div className={'w-2.5 h-2.5 rounded-full ' + dotConfig.color} />
        {dotConfig.pulse && (
          <div className={'absolute inset-0 w-2.5 h-2.5 rounded-full animate-ping opacity-75 ' + dotConfig.color} />
        )}
      </div>

      {/* Room number */}
      <div className="w-12 flex-shrink-0">
        <span className="text-sm font-bold text-white">{room.roomNumber}</span>
      </div>

      {/* Guest info */}
      <div className="flex-1 min-w-0">
        {guestName ? (
          <>
            <div className="text-sm text-white truncate">{guestName}</div>
            {displayStay && (
              <div className="text-xs text-slate-400 flex items-center gap-1">
                <span className={room.status === 'RESERVED' ? 'text-purple-300' : ''}>
                  {formatShortDate(displayStay.checkInDate)}
                </span>
                <span className="text-slate-600">{'→'}</span>
                <span className={isCheckoutToday ? 'text-yellow-400 font-medium' : ''}>
                  {formatShortDate(displayStay.checkOutDate)}
                </span>
                {isCheckoutToday && <span className="text-yellow-400">{'\u23F0'}</span>}
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-slate-500 italic">
            {room.status === 'CLEANING' ? 'Cleaning in progress' :
             room.status === 'OUT_OF_SERVICE' ? 'Out of service' : 'Available'}
          </div>
        )}
      </div>

      {/* Activity count badge */}
      {activityCount > 0 && (
        <div className="flex-shrink-0 text-xs text-slate-500 bg-slate-900 px-1.5 py-0.5 rounded">
          {activityCount}
        </div>
      )}

      {/* Chevron */}
      <svg className={'w-4 h-4 flex-shrink-0 transition-colors ' + (isSelected ? 'text-emerald-400' : 'text-slate-600')} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
}
